"use client";

import { useState, useMemo, useEffect } from 'react';
import { callAiFlow } from '@/lib/ai-client';
import type { CarServiceSuggestionsOutput } from '@/ai/flows/car-service-suggestions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Wrench,
  Warehouse,
  Banknote,
  CircleCheck,
  CreditCard,
  Search,
  Star,
  MapPin,
  MessageCircle,
  Phone,
  MessageSquare,
  Sparkles,
  Loader2,
  CircleAlert,
  Landmark,
  Grid3X3,
  ArrowRight,
  BatteryCharging,
  Disc3,
  Snowflake,
  Paintbrush,
  Cog,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCountry, COUNTRIES } from '@/context/CountryContext';
import { workshopsApi } from '@/lib/api/workshops';
import type { WorkshopServiceTemplate } from '@/lib/api/workshops';
import { getStoredAccessToken } from '@/lib/auth/session';
import { useToast } from '@/hooks/use-toast';

type MaintenanceCategory = {
  id: string;
  label: string;
  name: string;
  image: string;
  Icon: LucideIcon;
};

const CATEGORIES: MaintenanceCategory[] = [
  {
    id: 'الكل',
    label: 'كل الورش',
    name: 'All Workshops',
    image: 'https://images.unsplash.com/photo-1615906655593-ad0386982a0f?q=80&w=900&auto=format&fit=crop',
    Icon: Wrench,
  },
  {
    id: 'المحرك',
    label: 'المحرك',
    name: 'Engine Service',
    image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?q=80&w=900&auto=format&fit=crop',
    Icon: Cog,
  },
  {
    id: 'كهرباء',
    label: 'كهرباء',
    name: 'Electrical',
    image: 'https://images.unsplash.com/photo-1621905251918-48416bd8575a?q=80&w=900&auto=format&fit=crop',
    Icon: BatteryCharging,
  },
  {
    id: 'الفرامل',
    label: 'الفرامل',
    name: 'Brakes',
    image: 'https://images.unsplash.com/photo-1625047509248-ec889cbff17f?q=80&w=900&auto=format&fit=crop',
    Icon: Disc3,
  },
  {
    id: 'تكييف',
    label: 'تكييف',
    name: 'A/C Service',
    image: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?q=80&w=900&auto=format&fit=crop',
    Icon: Snowflake,
  },
  {
    id: 'سمكرة',
    label: 'سمكرة',
    name: 'Body & Paint',
    image: 'https://images.unsplash.com/photo-1607860108855-64acf2078ed9?q=80&w=900&auto=format&fit=crop',
    Icon: Paintbrush,
  },
];

interface Workshop {
  id: number | string;
  name?: string;
  name_ar?: string;
  description?: string;
  description_ar?: string;
  location?: string;
  phone?: string;
  image?: string;
  category?: string;
  rating?: string | number;
  workshop_store?: { store_name: string; store_name_ar: string };
}

type WorkshopPaymentMethod = 'cash' | 'card' | 'bank';

const WORKSHOP_PAYMENT_METHODS: Array<{ value: WorkshopPaymentMethod; label: string; hint: string; Icon: LucideIcon }> = [
  { value: 'cash', label: 'في الورشة', hint: 'الدفع عند الحضور', Icon: Banknote },
  { value: 'card', label: 'بطاقة', hint: 'يبقى معلقاً حتى ربط البوابة', Icon: CreditCard },
  { value: 'bank', label: 'تحويل', hint: 'تتم مراجعته من الورشة', Icon: Landmark },
];

export default function MaintenanceClient() {
  const router = useRouter();
  const { toast } = useToast();
  const { selectedCountryCode } = useCountry();
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [workshopServices, setWorkshopServices] = useState<WorkshopServiceTemplate[]>([]);
  const [isWorkshopsLoading, setIsWorkshopsLoading] = useState(true);
  const [bookingWorkshopId, setBookingWorkshopId] = useState<string | null>(null);
  const [selectedServiceByWorkshop, setSelectedServiceByWorkshop] = useState<Record<string, string>>({});
  const [paymentByWorkshop, setPaymentByWorkshop] = useState<Record<string, WorkshopPaymentMethod>>({});
  const [notesByWorkshop, setNotesByWorkshop] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiResult, setAiResult] = useState<CarServiceSuggestionsOutput | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('الكل');
  const [browseStep, setBrowseStep] = useState<'categories' | 'items'>('categories');

  useEffect(() => {
    setIsWorkshopsLoading(true);
    Promise.allSettled([
      workshopsApi.getWorkshops({ country_code: selectedCountryCode }),
      workshopsApi.getServices({ country_code: selectedCountryCode }),
    ])
      .then(([workshopsResult, servicesResult]) => {
        if (workshopsResult.status === 'fulfilled') {
          const data = workshopsResult.value;
          setWorkshops(Array.isArray(data) ? data : data.results || []);
        } else {
          console.error('Failed to load workshops from Django API:', workshopsResult.reason);
          setWorkshops([]);
        }
        if (servicesResult.status === 'fulfilled') {
          const data = servicesResult.value;
          setWorkshopServices(Array.isArray(data) ? data : data.results || []);
        } else {
          setWorkshopServices([]);
        }
      })
      .finally(() => setIsWorkshopsLoading(false));
  }, [selectedCountryCode]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search).get('search');
    if (query?.trim()) {
      setSearchQuery(query.trim());
      setBrowseStep('items');
    }
  }, []);

  const handleGetSuggestions = async () => {
    if (!problem.trim()) return;
    setLoading(true);
    try {
      const output = await callAiFlow<{ problemDescription: string }, CarServiceSuggestionsOutput>(
        'car-service-suggestions',
        { problemDescription: problem }
      );
      setAiResult(output);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredWorkshops = useMemo(() => {
    return workshops.filter(w => {
      const name = w.name_ar || w.name || '';
      const desc = w.description_ar || w.description || '';
      const cat = w.category || '';
      const matchesSearch = name.includes(searchQuery) || desc.includes(searchQuery);
      const matchesCategory = selectedCategory === 'الكل' || cat.includes(selectedCategory);
      return matchesSearch && matchesCategory;
    });
  }, [workshops, searchQuery, selectedCategory]);

  const handleCategorySelect = (category: MaintenanceCategory) => {
    setSelectedCategory(category.id);
    setBrowseStep('items');
  };

  const backToCategories = () => {
    setSelectedCategory('الكل');
    setSearchQuery('');
    setBrowseStep('categories');
  };

  const selectedCategoryInfo = CATEGORIES.find((category) => category.id === selectedCategory) || CATEGORIES[0];
  const workshopName = (w: Workshop) => w.name_ar || w.name || w.workshop_store?.store_name_ar || 'ورشة';
  const workshopDesc = (w: Workshop) => w.description_ar || w.description || 'لا يوجد وصف متوفر لهذه الورشة.';
  const workshopCat = (w: Workshop) => w.category || 'عام';
  const servicesForWorkshop = (w: Workshop) => workshopServices.filter((service) => String(service.workshop_store) === String(w.id));

  const handleBookWorkshopService = async (workshop: Workshop, serviceId: string) => {
    if (!serviceId) {
      toast({ title: 'اختر خدمة', description: 'يرجى اختيار خدمة الورشة قبل إرسال الطلب.' });
      return;
    }
    if (!getStoredAccessToken()) {
      toast({ title: 'تسجيل الدخول مطلوب', description: 'يرجى تسجيل الدخول لإرسال طلب الورشة.' });
      router.push('/login');
      return;
    }

    const key = String(workshop.id);
    setBookingWorkshopId(key);
    try {
      await workshopsApi.createOrder({
        service: Number(serviceId),
        payment_method: paymentByWorkshop[key] || 'cash',
        notes: notesByWorkshop[key] || '',
      });
      setNotesByWorkshop((prev) => ({ ...prev, [key]: '' }));
      toast({ title: 'تم إرسال طلب الورشة', description: 'سيظهر الطلب لدى الورشة مع حالة دفع معلقة حتى التأكيد.' });
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'تعذر إرسال الطلب. حاول مرة أخرى.';
      toast({ title: 'فشل الحجز', description: message });
    } finally {
      setBookingWorkshopId(null);
    }
  };

  return (
    <div className="space-y-6 pb-24">
      <section className="bg-white p-6 rounded-[2.5rem] shadow-sm border space-y-4">
        <div className="flex items-center gap-3">
          <div className="bg-orange-100 p-3 rounded-2xl text-primary">
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold font-headline">المساعد الذكي للصيانة</h2>
            <p className="text-sm text-muted-foreground">أدخل المشكلة وسنقترح عليك الحلول والورش</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="مثال: صوت طقطقة في المحرك..."
            className="rounded-2xl h-14 bg-slate-50 border-none shadow-inner"
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
          />
          <Button
            className="h-14 rounded-2xl px-8 bg-primary shadow-lg shadow-primary/20"
            disabled={loading || !problem.trim()}
            onClick={handleGetSuggestions}
          >
            {loading ? <Loader2 className="animate-spin h-5 w-5" /> : <Search className="h-5 w-5" />}
          </Button>
        </div>

        {aiResult && (
          <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-2 duration-300">
            <div className="bg-blue-50/50 p-4 rounded-3xl border border-blue-100">
              <h3 className="text-sm font-bold text-secondary mb-3 flex items-center gap-2">
                <CircleCheck className="h-4 w-4" /> الخدمات المقترحة
              </h3>
              <div className="flex flex-wrap gap-2">
                {aiResult.suggestedServices.map((s, i) => (
                  <Badge key={i} className="bg-white text-slate-700 hover:bg-white border-none shadow-sm rounded-xl px-3 py-1">
                    {s}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="bg-orange-50/50 p-4 rounded-3xl border border-orange-100">
              <h3 className="text-sm font-bold text-primary mb-3 flex items-center gap-2">
                <Warehouse className="h-4 w-4" /> ورش متخصصة
              </h3>
              <div className="flex flex-wrap gap-2">
                {aiResult.suggestedWorkshops.map((w, i) => (
                  <Badge key={i} className="bg-white text-slate-700 hover:bg-white border-none shadow-sm rounded-xl px-3 py-1">
                    {w}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex flex-col gap-4">
          <div className="relative group">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 h-5 w-5 transition-colors group-focus-within:text-primary" />
            <Input
              className="pr-12 rounded-2xl h-14 bg-white border-none shadow-sm text-sm"
              placeholder="ابحث عن اسم ورشة أو خدمة معينة..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value.trim()) setBrowseStep('items');
              }}
            />
          </div>

          {browseStep === 'items' && (
            <div className="space-y-3 rounded-[1.5rem] bg-white p-3 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <Badge className="rounded-full bg-primary px-4 py-2 text-white">
                  {selectedCategoryInfo.label}
                </Badge>
                <Button variant="outline" className="rounded-2xl shrink-0 gap-2" onClick={backToCategories}>
                  <ArrowRight className="h-4 w-4" />
                  التصنيفات
                </Button>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {CATEGORIES.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category)}
                    className={`shrink-0 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                      selectedCategory === category.id ? 'border-primary bg-primary text-white' : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-primary hover:text-primary'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {browseStep === 'categories' ? (
          <div className="space-y-4">
            <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">
              <Grid3X3 className="h-6 w-6 text-primary" />
              تصفح حسب التصنيف
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {CATEGORIES.map((category) => {
                const CategoryIcon = category.Icon;
                return (
                  <button key={category.id} onClick={() => handleCategorySelect(category)} className="group text-right">
                    <div className="relative h-36 overflow-hidden rounded-[1.5rem] bg-white shadow-sm ring-1 ring-slate-100 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:shadow-xl">
                      <img src={category.image} alt={category.label} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-slate-950/80 via-slate-950/15 to-transparent" />
                      <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white/20 to-transparent" />
                      <div className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-2xl bg-white/95 text-primary shadow-sm backdrop-blur">
                        <CategoryIcon className="h-4 w-4" />
                      </div>
                      <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                        <div className="mb-2 inline-flex h-7 w-7 items-center justify-center rounded-xl bg-white/20 text-white shadow-sm backdrop-blur-md ring-1 ring-white/25">
                          <CategoryIcon className="h-3.5 w-3.5" />
                        </div>
                        <h3 className="truncate text-sm font-black sm:text-base">{category.label}</h3>
                        <p className="mt-0.5 truncate text-[11px] font-semibold text-white/75">{category.name}</p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between rounded-full bg-slate-50 px-3 py-1.5 text-[11px] font-bold text-slate-500">
                      <span>عرض الورش</span>
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-primary shadow-sm">
                        <ArrowRight className="h-3.5 w-3.5" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : isWorkshopsLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="mt-4 text-slate-500 font-bold">جاري تحميل الورش...</p>
          </div>
        ) : filteredWorkshops.length === 0 ? (
          <div className="bg-white p-12 rounded-[2.5rem] border-none shadow-sm text-center space-y-4">
            <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto">
              <CircleAlert className="h-10 w-10 text-slate-300" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">لا توجد ورش متوفرة</h3>
            <p className="text-sm text-slate-400 max-w-xs mx-auto">
              عذراً، لا توجد ورش متوفرة في {COUNTRIES.find(c => c.code === selectedCountryCode)?.name} حالياً. جرب اختيار دولة أخرى أو تغيير معايير البحث.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredWorkshops.map((workshop) => {
              const workshopKey = String(workshop.id);
              const availableServices = servicesForWorkshop(workshop);
              const selectedServiceId = selectedServiceByWorkshop[workshopKey] || String(availableServices[0]?.id || '');
              const selectedService = availableServices.find((service) => String(service.id) === selectedServiceId);
              const selectedPayment = paymentByWorkshop[workshopKey] || 'cash';
              return (
              <Dialog key={workshop.id}>
                <DialogTrigger asChild>
                  <Card className="rounded-[2rem] border-none shadow-sm bg-white overflow-hidden hover:shadow-md transition-all cursor-pointer group">
                    <div className="flex p-4 gap-4">
                      <div className="w-24 h-24 rounded-2xl overflow-hidden shrink-0 border">
                        <img src={workshop.image || 'https://picsum.photos/seed/workshop/200/200'} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-1">
                          <h3 className="font-bold text-slate-800 truncate">{workshopName(workshop)}</h3>
                          <div className="flex items-center gap-1 text-yellow-500">
                            <Star className="h-3 w-3 fill-current" />
                            <span className="text-[10px] font-bold">{workshop.rating || '4.5'}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-slate-400 text-[10px] mb-2">
                          <MapPin className="h-3 w-3" />
                          {workshop.location || 'متوفر'}
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="secondary" className="bg-blue-50 text-blue-600 border-none text-[9px] px-2">
                            {workshopCat(workshop)}
                          </Badge>
                          <Badge variant="secondary" className="bg-green-50 text-green-600 border-none text-[9px] px-2">
                            متاح الآن
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </Card>
                </DialogTrigger>
                <DialogContent className="rounded-[2.5rem] p-0 overflow-hidden border-none max-w-md">
                  <div className="h-48 relative">
                    <img src={workshop.image || 'https://picsum.photos/seed/workshop/600/400'} className="w-full h-full object-cover" alt="" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 right-4 text-white">
                      <h2 className="text-xl font-bold">{workshopName(workshop)}</h2>
                      <div className="flex items-center gap-1 text-xs opacity-80">
                        <MapPin className="h-3 w-3" /> {workshop.location || 'متوفر'}
                      </div>
                    </div>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="space-y-2">
                      <h4 className="font-bold text-sm text-slate-800">عن الورشة</h4>
                      <p className="text-xs text-slate-500 leading-relaxed">{workshopDesc(workshop)}</p>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-slate-50 p-3 rounded-2xl text-center">
                        <Star className="h-4 w-4 text-yellow-500 mx-auto mb-1" />
                        <span className="block text-[10px] font-bold">{workshop.rating || '4.5'} تقييم</span>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-2xl text-center">
                        <CircleCheck className="h-4 w-4 text-green-500 mx-auto mb-1" />
                        <span className="block text-[10px] font-bold">معتمد</span>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-2xl text-center">
                        <Wrench className="h-4 w-4 text-primary mx-auto mb-1" />
                        <span className="block text-[10px] font-bold">{workshopCat(workshop)}</span>
                      </div>
                    </div>

                    {availableServices.length > 0 && (
                      <div className="rounded-3xl border border-blue-100 bg-blue-50/70 p-4 space-y-3">
                        <div>
                          <h4 className="font-bold text-sm text-slate-800">حجز خدمة من الورشة</h4>
                          <p className="text-[11px] text-slate-500 mt-1">يرسل الطلب مباشرة إلى لوحة الورشة مع حالة دفع معلقة.</p>
                        </div>
                        <select
                          value={selectedServiceId}
                          onChange={(e) => setSelectedServiceByWorkshop((prev) => ({ ...prev, [workshopKey]: e.target.value }))}
                          className="h-11 w-full rounded-2xl border border-blue-100 bg-white px-3 text-sm font-bold text-slate-800 outline-none"
                        >
                          {availableServices.map((service) => (
                            <option key={service.id} value={service.id}>
                              {service.name_ar || service.name} - {service.price}
                            </option>
                          ))}
                        </select>
                        {selectedService && (
                          <p className="text-xs text-slate-600">
                            السعر: <span className="font-black text-blue-700">{selectedService.price}</span>
                            {' '}• المدة: {selectedService.duration_minutes || 60} دقيقة
                          </p>
                        )}
                        <div className="grid grid-cols-3 gap-2">
                          {WORKSHOP_PAYMENT_METHODS.map(({ value, label, hint, Icon }) => {
                            const active = selectedPayment === value;
                            return (
                              <button
                                key={value}
                                type="button"
                                onClick={() => setPaymentByWorkshop((prev) => ({ ...prev, [workshopKey]: value }))}
                                className={`rounded-2xl border p-2 text-right transition-all ${
                                  active ? 'border-primary bg-white shadow-sm' : 'border-blue-100 bg-white/70'
                                }`}
                              >
                                <div className="flex items-center gap-1 text-[11px] font-black text-slate-800">
                                  <Icon className={`h-3.5 w-3.5 ${active ? 'text-primary' : 'text-slate-400'}`} />
                                  {label}
                                </div>
                                <p className="mt-1 line-clamp-2 text-[10px] leading-4 text-slate-500">{hint}</p>
                              </button>
                            );
                          })}
                        </div>
                        <Input
                          placeholder="ملاحظات الحجز (اختياري)"
                          value={notesByWorkshop[workshopKey] || ''}
                          onChange={(e) => setNotesByWorkshop((prev) => ({ ...prev, [workshopKey]: e.target.value }))}
                          className="rounded-2xl bg-white"
                        />
                        <Button
                          className="w-full rounded-2xl h-11 bg-primary"
                          disabled={bookingWorkshopId === workshopKey}
                          onClick={() => handleBookWorkshopService(workshop, selectedServiceId)}
                        >
                          {bookingWorkshopId === workshopKey ? <Loader2 className="h-4 w-4 animate-spin" /> : 'إرسال طلب الحجز'}
                        </Button>
                      </div>
                    )}

                    <div className="space-y-3">
                      <h4 className="font-bold text-sm text-slate-800">تواصل مباشر</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <Button className="rounded-2xl h-12 bg-primary flex gap-2" asChild>
                          <Link href={`/chat`}>
                            <MessageSquare className="h-4 w-4" /> الدردشة
                          </Link>
                        </Button>
                        <Button variant="outline" className="rounded-2xl h-12 border-green-500 text-green-600 hover:bg-green-50 flex gap-2" asChild>
                          <a href={`https://wa.me/${workshop.phone || ''}`} target="_blank">
                            <MessageCircle className="h-4 w-4" /> واتساب
                          </a>
                        </Button>
                      </div>
                      <Button variant="outline" className="w-full rounded-2xl h-12 flex gap-2 border-slate-200" asChild>
                        <a href={`tel:${workshop.phone || ''}`}>
                          <Phone className="h-4 w-4 text-secondary" /> مكالمة هاتفية
                        </a>
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
